import { useEffect, useRef, useState } from 'react'

export default function PreviewCursor() {
  const dotRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<SVGSVGElement>(null)
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    document.body.classList.add('preview-body')
    const fine = window.matchMedia('(pointer: fine)').matches
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (!fine || reduced) {
      return () => {
        document.body.classList.remove('preview-body')
      }
    }

    setEnabled(true)
    document.body.classList.add('preview-cursor-on')

    let x = -100
    let y = -100
    let ringX = -100
    let ringY = -100
    let size = 36
    let targetSize = 36
    let raf = 0

    const onMove = (e: MouseEvent) => {
      x = e.clientX
      y = e.clientY
    }
    const onOver = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null
      targetSize = target && target.closest('a, button, [data-hover]') ? 86 : 36
    }
    const loop = () => {
      ringX += (x - ringX) * 0.16
      ringY += (y - ringY) * 0.16
      size += (targetSize - size) * 0.2
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`
      }
      if (ringRef.current) {
        // Redraw a vector circle at its actual size, without scaling a CSS
        // border or compositing a soft outline around it.
        const dpr = window.devicePixelRatio || 1
        const diameter = Math.round(size * dpr) / dpr
        const left = Math.round((ringX - diameter / 2) * dpr) / dpr
        const top = Math.round((ringY - diameter / 2) * dpr) / dpr
        ringRef.current.style.width = `${diameter}px`
        ringRef.current.style.height = `${diameter}px`
        ringRef.current.style.left = `${left}px`
        ringRef.current.style.top = `${top}px`
      }
      raf = requestAnimationFrame(loop)
    }

    window.addEventListener('mousemove', onMove, { passive: true })
    window.addEventListener('mouseover', onOver, { passive: true })
    raf = requestAnimationFrame(loop)

    return () => {
      document.body.classList.remove('preview-body')
      document.body.classList.remove('preview-cursor-on')
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseover', onOver)
      cancelAnimationFrame(raf)
    }
  }, [])

  if (!enabled) return null

  return (
    <>
      <div ref={dotRef} className="preview-cursor-dot" aria-hidden="true" />
      <svg
        ref={ringRef}
        className="preview-cursor-ring"
        viewBox="0 0 100 100"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </>
  )
}
